<!--
SPDX-FileCopyrightText: 2025 Land Baden-Württemberg <InnoLab@stm.bwl.de>

SPDX-License-Identifier: MPL-2.0
-->

<!--
  VoiceTextArea
  ─────────────
  Self-contained voice-input textarea. Manages the transcription connection,
  recording state, and text display.

  Props
  ─────
  placeholder  – shown when transcript is empty
  clearLabel   – label for the clear button (shown when transcript has content)
  wsUrl        – WebSocket endpoint; null/empty = use mock connection
  wsProtocols  – optional WebSocket subprotocols
  value        – transcript text (supports parent binding)
  buttonOnly   – render only the microphone button, without the textarea
  disabled     - (temporarily) disable starting a new recording

  Events
  ──────
  error – { detail: Error } forwarded from the connection layer

  Real WebSocket protocol (transcription-realtime microservice)
  ─────────────────────────────────────────────────────────────
  Client → server:
    { type: 'input_audio_buffer.append', audio: <base64 PCM16 @ 16 kHz> }  (streamed)
    { type: 'input_audio_buffer.commit' }

  Server → client (relevant subset):
    { type: 'session.started', transcription: { type, model } }
    { type: 'transcription.update', text, delta, end_time }
    { type: 'transcription.completed', text }
    { type: 'session.ended', reason }
    { type: 'error', error: { error_message } }
-->
<script>
import { afterUpdate, createEventDispatcher, onDestroy } from 'svelte';

/** @type {string} Placeholder text shown in the textarea when it is empty. */
export let placeholder = '';
/** @type {string} Label for the clear-all button. */
export let clearLabel = 'Clear';
/** Accessible label for the microphone button while idle. */
export let startLabel = 'Start recording';
/** Accessible label for the microphone button while recording. */
export let stopLabel = 'Stop recording';
/** @type {string|null} WebSocket URL of the transcription-realtime service. */
export let wsUrl = null;
/** @type {string[]} Optional WebSocket subprotocols. */
export let wsProtocols = [];
/** @type {string} Current transcript text. Supports parent binding. */
export let value = '';
/** @type {boolean} Render only the microphone control, without the textarea. */
export let buttonOnly = false;
/** @type {boolean} Disable starting a new recording. */
export let disabled = false;

/** Event dispatcher — emits `error` events to the parent component. */
const dispatch = createEventDispatcher();

// ── Constants ────────────────────────────────────────────────────────────────
/** Milliseconds the mic button must be held before switching to hold-to-talk mode. */
const HOLD_THRESHOLD_MS = 500;
/** Maximum time to wait until the backend confirms that the realtime session is ready. */
const SESSION_START_TIMEOUT_MS = 10000;
/** Maximum time to wait for final transcript/session.ended after stopping capture. */
const FINALIZATION_TIMEOUT_MS = 15000;

// ── State ────────────────────────────────────────────────────────────────────
// finalSegments: ordered list of {id, text}. The realtime backend currently emits
// one session-level final transcript, but this shape keeps manual editing simple.
let finalSegments = [];
/** Current live transcript from the server; replaced on each update event. */
let interimText = '';
/** True while the WebSocket is open but the backend session is not ready yet. */
let isStarting = false;
/** True while audio is being captured and streamed over the WebSocket. */
let isRecording = false;
/** True after stop is requested, while waiting for the server to flush final segments. */
let waitingForServer = false;
/** Reference to the &lt;textarea&gt; DOM element for auto-grow and scroll management. */
let textEl = null;
let lastSyncedValue = value;

// ── Auto-grow textarea ───────────────────────────────────────────────────────
afterUpdate(() => {
  if (textEl) {
    textEl.style.height = 'auto';
    textEl.style.height = textEl.scrollHeight + 'px';
  }
});

// ── Helpers ──────────────────────────────────────────────────────────────────
/**
 * Collapse whitespace and strip leading/trailing spaces from a string.
 * Returns an empty string for null/undefined input.
 * @param {string|null|undefined} s
 * @returns {string}
 */
function normalize(s) {
  return (s || '').trim().replace(/\s+/g, ' ');
}

$: finalText = finalSegments
  .map((s) => s.text)
  .join(' ')
  .replace(/\s+/g, ' ')
  .trim();

$: if (!isRecording && !isStarting && !waitingForServer && value !== lastSyncedValue) {
  lastSyncedValue = value;
  finalSegments = value ? [{ id: '_external', text: value }] : [];
}

// What the textarea displays: show interim appended while active, just final text when done
$: displayValue =
  isRecording || isStarting || waitingForServer
    ? finalText && interimText
      ? finalText + ' ' + interimText
      : finalText || interimText
    : finalText;

$: syncValue(displayValue);

function syncValue(nextValue) {
  if (value === nextValue && lastSyncedValue === nextValue) return;
  value = nextValue;
  lastSyncedValue = nextValue;
}

// ── Incoming messages from connection ────────────────────────────────────────
/**
 * Handle a normalised message from either the mock or real connection.
 * - `final`   — append or replace a confirmed segment by its ID.
 * - `interim` — update the live hypothesis shown while recording.
 * @param {{ type: 'final'|'interim', text: string, segmentId?: string }} msg
 */
function handleMessage(msg) {
  if (msg.type === 'final') {
    const id = msg.segmentId || 'seg_' + Date.now();
    const text = normalize(msg.text);
    if (!text) return;
    const idx = finalSegments.findIndex((s) => s.id === id);
    if (idx >= 0) {
      // Same segment arrived again — replace, don't append
      finalSegments[idx] = { id, text };
      finalSegments = [...finalSegments];
    } else {
      finalSegments = [...finalSegments, { id, text }];
    }
  } else if (msg.type === 'interim') {
    interimText = normalize(msg.text);
  }
}

/** Preserve the last visible server update if the final event never arrives. */
function commitInterimFallback() {
  const text = normalize(interimText);
  if (!text) return;

  const existingFinal = normalize(finalText);
  if (existingFinal === text) {
    interimText = '';
    return;
  }

  finalSegments = [{ id: 'session_fallback', text }];
  interimText = '';
}

/**
 * Forward a connection error to the parent component via the `error` event.
 * @param {Error} err
 */
function handleError(err) {
  if (waitingForServer && interimText && !finalText) commitInterimFallback();
  isStarting = false;
  isRecording = false;
  waitingForServer = false;
  interimText = '';
  const currentConnection = connection;
  connection = null;
  if (currentConnection) currentConnection.abort();
  dispatch('error', err);
}

/**
 * Called when the connection closes (server or network).
 * Clears the waiting state while preserving the last visible text when finalisation timed out.
 */
function handleClose() {
  if (waitingForServer && interimText && !finalText) {
    commitInterimFallback();
  } else {
    interimText = '';
  }
  isStarting = false;
  waitingForServer = false;
}

// ── Real WebSocket connection ────────────────────────────────────────────────
/**
 * Create a real WebSocket connection to the transcription-realtime service.
 * Handles microphone capture, PCM16 encoding, and server event parsing.
 * @param {string} url  WebSocket endpoint URL.
 * @param {{ onMessage: Function, onError: Function, onClose: Function }} callbacks
 * @returns {{ open: Function, close: Function, abort: Function }}
 */
function createWsConnection(url, { onMessage, onError, onClose }) {
  const TARGET_SR = 16000;
  let ws = null;
  let mediaStream = null;
  let audioCtx = null;
  let sourceNode = null;
  let workletNode = null;
  let openingReject = null;
  let sessionStarted = false;
  let sessionStartedResolve = null;
  let sessionStartedReject = null;
  let sessionStartedTimeout = null;

  /**
   * Resample float32 audio to TARGET_SR and encode as base64 PCM16-LE.
   * @param {Float32Array} float32Array  Raw samples from the Web Audio API.
   * @param {number} sourceSampleRate   Sample rate reported by AudioContext.
   * @returns {string} Base64-encoded PCM16-LE bytes.
   */
  function float32ToPcm16Base64(float32Array, sourceSampleRate) {
    let samples = float32Array;
    if (sourceSampleRate !== TARGET_SR) {
      const ratio = sourceSampleRate / TARGET_SR;
      const out = new Float32Array(Math.max(1, Math.round(float32Array.length / ratio)));
      for (let i = 0; i < out.length; i++) {
        const src = i * ratio;
        const lo = Math.floor(src);
        const hi = Math.min(lo + 1, float32Array.length - 1);
        out[i] = float32Array[lo] * (1 - (src - lo)) + float32Array[hi] * (src - lo);
      }
      samples = out;
    }
    const pcm = new Int16Array(samples.length);
    for (let i = 0; i < samples.length; i++) {
      const s = Math.max(-1, Math.min(1, samples[i]));
      pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    const bytes = new Uint8Array(pcm.buffer);
    let bin = '';
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return btoa(bin);
  }

  /**
   * Send a JSON payload over the WebSocket. Drops and warns if not open.
   * @param {object} payload
   */
  function send(payload) {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(payload));
    } else {
      console.warn('[VTA] send DROPPED (ws not open)', payload.type, ws?.readyState);
    }
  }

  function clearSessionStartedWait() {
    if (sessionStartedTimeout) {
      clearTimeout(sessionStartedTimeout);
      sessionStartedTimeout = null;
    }
    sessionStartedResolve = null;
    sessionStartedReject = null;
  }

  function resolveSessionStarted() {
    sessionStarted = true;
    if (!sessionStartedResolve) return;
    const resolve = sessionStartedResolve;
    clearSessionStartedWait();
    resolve();
  }

  function rejectSessionStarted(err) {
    if (!sessionStartedReject) return false;
    const reject = sessionStartedReject;
    clearSessionStartedWait();
    reject(err);
    return true;
  }

  function waitForSessionStarted() {
    if (sessionStarted) return Promise.resolve();

    return new Promise((resolve, reject) => {
      sessionStartedResolve = resolve;
      sessionStartedReject = reject;
      sessionStartedTimeout = setTimeout(() => {
        rejectSessionStarted(new Error('Realtime transcription session did not start in time'));
      }, SESSION_START_TIMEOUT_MS);
    });
  }

  async function startAudioCapture() {
    mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: { channelCount: { ideal: 1 }, echoCancellation: true, noiseSuppression: true },
      video: false,
    });
    audioCtx = new AudioContext();
    console.log('[VTA] recording started, sampleRate=' + audioCtx.sampleRate);
    await audioCtx.audioWorklet.addModule('/static/realtime-audio-processor.js');
  }

  /** Stop microphone capture and tear down all Web Audio nodes. */
  function stopAudio() {
    if (workletNode) {
      workletNode.port.onmessage = null;
      workletNode.disconnect();
      workletNode = null;
    }
    if (sourceNode) {
      sourceNode.disconnect();
      sourceNode = null;
    }
    if (audioCtx) {
      audioCtx.close().catch(() => {});
      audioCtx = null;
    }
    if (mediaStream) {
      mediaStream.getTracks().forEach((t) => t.stop());
      mediaStream = null;
    }
  }

  /** Abort the current connection without committing audio or waiting for server finalisation. */
  function abortConnection() {
    stopAudio();
    if (openingReject) {
      const rejectOpening = openingReject;
      openingReject = null;
      const abortError = new Error('WebSocket connection aborted');
      abortError.name = 'AbortError';
      rejectOpening(abortError);
    }
    clearSessionStartedWait();
    if (!ws) return;
    const currentWs = ws;
    ws = null;
    currentWs.onclose = null;
    currentWs.onerror = null;
    currentWs.onmessage = null;
    if (currentWs.readyState === WebSocket.OPEN || currentWs.readyState === WebSocket.CONNECTING) {
      currentWs.close();
    }
  }

  /** Abort capture/transport and forward the error to the owning component. */
  function fail(err) {
    abortConnection();
    onError(err);
  }

  /**
   * Parse and dispatch a raw server WebSocket message.
   * Handles session.started, transcription.update, transcription.completed, session.ended and error event types.
   * @param {string} data  Raw JSON string from the server.
   */
  function handleServerMessage(data) {
    const evt = JSON.parse(data);
    if (evt.type === 'session.started') {
      console.debug('[VTA] session started', evt.transcription);
      resolveSessionStarted();
    } else if (evt.type === 'transcription.update') {
      const text = normalize(evt.text || '');
      if (text) {
        onMessage({ type: 'interim', text });
      }
    } else if (evt.type === 'transcription.completed') {
      const text = normalize(evt.text || '');
      onMessage({ type: 'final', text, segmentId: 'session_final' });
      onMessage({ type: 'interim', text: '' });
    } else if (evt.type === 'session.ended') {
      if (ws) {
        ws.close();
        ws = null;
      }
      onClose();
    } else if (evt.type === 'error') {
      console.error('[VTA] server error:', evt);
      const err = new Error(evt.error?.error_message || 'Transcription error');
      if (rejectSessionStarted(err)) {
        abortConnection();
      } else {
        fail(err);
      }
    }
  }

  return {
    async open() {
      console.log('[VTA] opening WS', url);
      try {
        ws = wsProtocols.length > 0 ? new WebSocket(url, wsProtocols) : new WebSocket(url);
        ws.onmessage = (e) => handleServerMessage(e.data);
        ws.onclose = (e) => {
          stopAudio();
          onClose();
        };

        await new Promise((resolve, reject) => {
          openingReject = reject;
          ws.onopen = () => {
            openingReject = null;
            resolve();
          };
          ws.onerror = (e) => {
            console.error('[VTA] ws error during open', e);
            openingReject = null;
            reject(new Error('WebSocket connection failed'));
          };
        });
        ws.onerror = (e) => {
          console.error('[VTA] ws error', e);
          fail(new Error('WebSocket error'));
        };

        await waitForSessionStarted();
        await startAudioCapture();
        sourceNode = audioCtx.createMediaStreamSource(mediaStream);
        workletNode = new AudioWorkletNode(audioCtx, 'realtime-audio-processor');

        workletNode.port.onmessage = (e) => {
          if (!ws || ws.readyState !== WebSocket.OPEN) return;
          const b64 = float32ToPcm16Base64(e.data, audioCtx.sampleRate);
          send({ type: 'input_audio_buffer.append', audio: b64 });
        };

        sourceNode.connect(workletNode);
        workletNode.connect(audioCtx.destination);
      } catch (err) {
        if (err?.name !== 'AbortError') abortConnection();
        throw err;
      }
    },

    close() {
      stopAudio();
      if (ws?.readyState === WebSocket.CONNECTING) {
        abortConnection();
        onClose();
        return;
      }
      send({ type: 'input_audio_buffer.commit' });
      if (ws) {
        // Give the server time to send session.ended before preserving the visible fallback.
        const forceClose = setTimeout(() => {
          console.warn('[VTA] forceClose — server session.ended not received in time');
          if (ws) {
            ws.close();
            ws = null;
          }
          onClose();
        }, FINALIZATION_TIMEOUT_MS);
        ws.onclose = (e) => {
          clearTimeout(forceClose);
          ws = null;
          onClose();
        };
      }
    },

    abort() {
      abortConnection();
    },
  };
}

// ── Recording control ────────────────────────────────────────────────────────
let connection = null;

/**
 * Start recording. Opens the connection (real or mock) and sets `isRecording`.
 * Errors during connection setup are forwarded via `handleError`.
 */
async function startRecording() {
  if (disabled || isRecording || isStarting) return;
  isStarting = true;
  connection = createWsConnection(wsUrl, { onMessage: handleMessage, onError: handleError, onClose: handleClose });
  try {
    await connection.open();
    isStarting = false;
    isRecording = true;
  } catch (err) {
    isStarting = false;
    if (err?.name === 'AbortError') {
      connection = null;
      return;
    }
    handleError(err);
  }
}

/**
 * Stop recording. For real connections, `waitingForServer` keeps the textarea
 * read-only until the server sends `session.ended` with the definitive final.
 */
function stopRecording() {
  if (isStarting) {
    isStarting = false;
    if (connection) {
      connection.abort();
      connection = null;
    }
    return;
  }
  if (!isRecording) return;
  isRecording = false;
  // Don't commit interimText here — the server will send the definitive final event.
  // waitingForServer keeps the textarea readonly and interim visible until session.ended.
  waitingForServer = true;
  if (connection) {
    connection.close();
    connection = null;
  }
}

/** Stop recording and reset all transcript state. */
function clearAll() {
  stopRecording();
  waitingForServer = false;
  finalSegments = [];
  interimText = '';
}

/**
 * Called when the user edits the textarea directly.
 * Collapses all segments into a single edited segment so subsequent
 * server events don't overwrite manual edits.
 * @param {Event} e
 */
function onTextInput(e) {
  finalSegments = e.target.value ? [{ id: '_edited', text: e.target.value }] : [];
}

// ── Click vs. hold (mic button) ──────────────────────────────────────────────
let holdTimer = null;
/** True when the mic button is held down (hold-to-talk mode active). */
let isHoldMode = false;

/**
 * Start the hold timer on mouse/touch down.
 * If held for HOLD_THRESHOLD_MS, switches to hold-to-talk mode.
 * @param {Event} e
 */
function onMicDown(e) {
  e.preventDefault();
  if (disabled) return;
  holdTimer = setTimeout(() => {
    isHoldMode = true;
    if (!isStarting && !isRecording) startRecording();
  }, HOLD_THRESHOLD_MS);
}

/**
 * On release: cancel the hold timer if still pending.
 * In hold mode, stop recording. In click mode, toggle recording.
 */
function onMicUp() {
  if (disabled) return;
  if (holdTimer) {
    clearTimeout(holdTimer);
    holdTimer = null;
  }
  if (isHoldMode) {
    isHoldMode = false;
    stopRecording();
  } else {
    isStarting || isRecording ? stopRecording() : startRecording();
  }
}

/**
 * If the pointer leaves the button while in hold mode, stop recording
 * as if the button had been released.
 */
function onMicLeave() {
  if (isHoldMode) {
    isHoldMode = false;
    if (holdTimer) {
      clearTimeout(holdTimer);
      holdTimer = null;
    }
    stopRecording();
  }
}

onDestroy(() => {
  if (holdTimer) clearTimeout(holdTimer);
  if (connection) {
    if (isStarting) {
      connection.abort();
    } else {
      connection.close();
    }
    connection = null;
  }
});
</script>

{#if buttonOnly}
  <div class="vta-inline-control">
    {#if isStarting || isRecording || waitingForServer}
      <div class="vta-pill inline-pill" aria-hidden="true">
        <span class="dot" />
        <span class="dot" />
        <span class="dot" />
      </div>
    {/if}

    <button
      class="vta-mic"
      class:recording={isRecording}
      class:starting={isStarting}
      aria-pressed={isRecording || isStarting}
      aria-label={isRecording || isStarting ? stopLabel : startLabel}
      title={isRecording || isStarting ? stopLabel : startLabel}
      {disabled}
      on:mousedown={onMicDown}
      on:mouseup={onMicUp}
      on:mouseleave={onMicLeave}
      on:touchstart|preventDefault={onMicDown}
      on:touchend={onMicUp}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
        <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
      </svg>
    </button>
  </div>
{:else}
  <div class="vta-wrapper">

    <textarea
      class="vta-textarea"
      bind:this={textEl}
      value={displayValue}
      on:input={onTextInput}
      placeholder={placeholder}
      readonly={isRecording || isStarting || waitingForServer}
      spellcheck="true"
    ></textarea>

    <!-- bottom bar -->
    <div class="vta-bar">
      <div class="vta-indicator">
        {#if isStarting || isRecording || waitingForServer}
          <div class="vta-pill">
            <span class="dot" />
            <span class="dot" />
            <span class="dot" />
          </div>
        {/if}
      </div>

      <button
      class="vta-mic"
      class:recording={isRecording}
      class:starting={isStarting}
      aria-pressed={isRecording || isStarting}
      aria-label={isRecording || isStarting ? stopLabel : startLabel}
      title={isRecording || isStarting ? stopLabel : startLabel}
      {disabled}
      on:mousedown={onMicDown}
      on:mouseup={onMicUp}
      on:mouseleave={onMicLeave}
      on:touchstart|preventDefault={onMicDown}
      on:touchend={onMicUp}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
        <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
      </svg>
    </button>
    </div>

  </div>
{/if}

{#if !buttonOnly && finalText && !isStarting && !isRecording && !waitingForServer}
  <div class="vta-actions">
    <button class="secondary" on:click={clearAll}>{clearLabel}</button>
  </div>
{/if}

<style>
  .vta-wrapper {
    width: 100%;
    border: 1.5px solid #ccc;
    border-radius: 6px;
    background: #fff;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    transition: border-color 0.15s;
  }

  .vta-wrapper:focus-within {
    border-color: var(--main-color, #555);
  }

  .vta-textarea {
    padding: 16px 20px 10px 20px;
    overflow: hidden;
    font-size: 1rem;
    font-family: inherit;
    line-height: 1.75;
    word-break: break-word;
    white-space: pre-wrap;
    min-height: 180px;
    border: none;
    outline: none;
    resize: none;
    background: transparent;
    color: #111;
    width: 100%;
    box-sizing: border-box;
  }

  .vta-textarea[readonly] { cursor: default; }

  .vta-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px 10px 16px;
    border-top: 1px solid #f0f0f0;
    background: #fafafa;
  }

  .vta-indicator {
    min-height: 36px;
    display: flex;
    align-items: center;
  }

  .vta-pill {
    display: flex;
    align-items: center;
    gap: 5px;
    background: #fff;
    border-radius: 14px;
    padding: 7px 14px;
    box-shadow: 0 1px 8px rgba(0, 0, 0, 0.14);
  }

  .dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #555;
    animation: vta-bounce 0.9s ease-in-out infinite;
    display: inline-block;
  }

  .dot:nth-child(2) { animation-delay: 0.15s; }
  .dot:nth-child(3) { animation-delay: 0.30s; }

  @keyframes vta-bounce {
    0%, 60%, 100% { transform: translateY(0); }
    30%           { transform: translateY(-7px); }
  }

  .vta-mic {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: none;
    background: var(--main-color, #333);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    flex-shrink: 0;
    transition: background 0.15s, transform 0.1s;
    user-select: none;
  }

  .vta-mic:hover           { filter: brightness(1.15); }
  .vta-mic:focus           { outline: none; box-shadow: none; }
  .vta-mic.recording       { background: #c0392b; transform: scale(1.06); }
  .vta-mic.starting        { background: #6b7280; }
  .vta-mic.recording:hover { filter: brightness(1.1); }
  .vta-mic:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  .vta-inline-control {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  .vta-inline-control .vta-mic {
    width: 40px;
    height: 40px;
    background: #f9f9f9;
    color: #555;
    border: 1px solid #e0e0e0;
  }

  .vta-inline-control .vta-mic:hover:not(:disabled) {
    background: #f0f0f0;
    color: #222;
    filter: none;
  }

  .vta-inline-control .vta-mic.recording {
    background: #c0392b;
    color: #fff;
    border-color: #c0392b;
  }

  .inline-pill {
    padding: 7px 10px;
  }

  .vta-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 10px;
    width: 100%;
  }
</style>
